import { LO_DOMAIN, PROD_DOMAINS, STATIC_CDN_DOMAIN } from '../constants/domains';
import { UrlsEnum } from '../enums/route.enum';
import { ActivatedRoute, Params } from '@angular/router';

export const isUrlValid = (url: string): boolean => {
  const pattern = /(ftp|http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/;
  return pattern.test(url);
};

export const getPathNameAfterDomain = (url: string): string => {
  const splitUrl = splitBySlash(url);
  const domainIndex = splitUrl.findIndex((e) => e === LO_DOMAIN);
  return splitUrl.slice(domainIndex + 1).join('/');
};

export const removeQueryParamsFromUrl = (url: string): string => {
  return url && url.includes('?') ? url.split('?')[0] : url;
};

export const isAbsolutePath = (path: string): boolean => {
  return path && path.toLocaleLowerCase().indexOf('://') > -1;
};

export const composeAbsoluteUrl = (origin: string, link: string): string => {
  if (link && link.charAt(0) === '/') {
    return origin + link;
  } else {
    return `${origin}/${link}`;
  }
};

export const getQueryValue = (url: string, query: string): string => {
  const match = url.match(`[?&]${query}=([^&]+)`);
  return match ? match[1] : null;
};

export const getFirstSegment = (url: string): string => {
  return url.split('/').filter(Boolean)[0];
};

export const splitBySlash = (url: string): string[] => {
  return url.substr(1).split('/');
};

export const isProdUrl = (url: string): boolean => {
  return PROD_DOMAINS.some((domain: string) => url.includes(domain));
};

export const isCDNUrl = (url: string, cdnPath: string): boolean => {
  return [cdnPath, STATIC_CDN_DOMAIN].some((cdn: string) => url.includes(cdn));
};

export const tryToDecodeURI = (value: string): string | null => {
  if (!value) {
    return null;
  }
  try {
    return decodeURI(value);
  } catch (error) {
    return value;
  }
};

export const getLastUrlSegment = (url: string): string => {
  return url.substring(url.lastIndexOf('/') + 1);
};

// Преобразует Url в CamelCase имя, к примеру из "pages/types/1/widgets" получаем "pagesTypesWidgets"
export const getCamelCaseNameFromUrl = (url: string): string => {
  return url
    .split('/')
    .filter((part) => part && isNaN(Number(part)))
    .map((part, index) =>
      index === 0 ? part.toLowerCase() : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
    )
    .join('');
};

// Для /catalog/alkogol-17036/?filter.brand=martini-10735072/ вернёт /catalog/alkogol-17036/?filter.brand=martini-10735072
export const removeLastSlash: (url: string) => string = (url: string): string => {
  if (!url || url === UrlsEnum.Base) return url;

  return url.replace(/\/$/, '');
};

// Для /catalog/alkogol-17036/ вернёт catalog/alkogol-17036/
export const removeFirstSlash: (url: string) => string = (url: string): string => {
  return url.replace(/^\//, '');
};

// Для catalog/alkogol-17036/ вернёт /catalog/alkogol-17036/
export const addFirstSlash: (url: string) => string = (url: string): string => {
  if (url.startsWith('/')) return url;
  return `/${url}`;
};

// Убирает все ведущие и завершающие слеши из пути (например: ///path/// → path)
export const trimPathSlashes: (path: string) => string = (path: string): string => {
  return path.replace(/^\/+/, '').replace(/\/+$/, '');
};

export const ensureTrailingSlash: (url: string) => string = (url: string): string => {
  if (url.endsWith('/')) return url;
  return url + '/';
};

export const hasTrailingSlash: (url: string) => boolean = (url: string): boolean => {
  return url.endsWith('/');
};

export const getPathFromUrl = (url: string): string => {
  if (!url) {
    return '';
  }
  if (url.includes('://')) {
    const urlObj = new URL(url);
    return urlObj.pathname;
  }

  return url.split('?')[0].split('#')[0];
};

export const removeQuestionMark: (url: string) => string = (url: string): string => {
  if (url.startsWith('?')) {
    return url.substring(1);
  }

  return url;
};

export const getQueryParamsString = (route: ActivatedRoute) => {
  let queryParams: string = '?';

  if (route?.snapshot?.root?.queryParams) {
    for (const [key, value] of Object.entries(route.snapshot.root.queryParams)) {
      queryParams += `${key}=${value}&`;
    }
  }

  return queryParams.slice(0, queryParams.length - 1);
};

export const getParamsFromString = (paramsString: string): Params => {
  const queryParams: string[] = paramsString.split('&');
  const params: Params = {};

  if (queryParams.length) {
    queryParams.forEach((queryParam: string): void => {
      const paramKeyAndValue: string[] = queryParam.split('=');

      if (paramKeyAndValue.length === 2) {
        params[paramKeyAndValue[0]] = paramKeyAndValue[1];
      }
    });
  }

  return params;
};
